/**
 * Inline product creation (TASK-0191), shared by the invoice import review and the PO's invoice
 * screen (TASK-0236 moved it here from `purchasing.import.tsx`).
 *
 * Replaces the Svelte admin's "+ Create product" link → new-tab `/products/new?...` round trip.
 * Same prefill, same two-call contract (`POST /products` then a best-effort
 * `POST /vendors/:id/items`), but on this screen and auto-attached — see `createProductAndLink`
 * in `features/buying/api.ts` for the shared logic.
 */
import { useEffect, useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/api/client';
import { api } from '@/lib/api/client';
import type { ProductSummary } from '@/lib/api/types';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Consequences } from '@/ui/Callout';
import { Dialog } from '@/ui/Dialog';
import { Checkbox, Field, Input, MoneyInput, Select } from '@/ui/Field';
import { activeFamilies, useFamilies } from '@/features/catalog/families';
import { LinkPicker } from '@/features/catalog/InputLinksPanel';
import { createProductAndLink, derivedPerEachCents } from './api';
import { INPUT_CONSEQUENCE } from './NewInputProductDialog';
import type { CreatedProduct, CreateProductPrefill } from './types';

/** What the dialog reads from the line it was opened from (an import review line satisfies it). */
export type CreateProductSource = {
  key: number;
  description: string;
  upc: string;
  sku: string;
  unitsPerCase: number | null;
  /** Per each, or per case when `caseMode` is on. */
  unitCostCents: number;
  caseMode: boolean;
  unit: 'ea' | 'lb';
};

export function CreateProductDialog({
  open,
  vendorId,
  line,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  vendorId: number | null;
  /** The line the dialog was opened from; null closes it defensively. */
  line: CreateProductSource | null;
  onOpenChange: (open: boolean) => void;
  onCreated: (key: number, product: CreatedProduct) => void;
}) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [barcode, setBarcode] = useState('');
  const [vendorSku, setVendorSku] = useState('');
  const [unitsPerCase, setUnitsPerCase] = useState<number | null>(null);
  // TASK-0259 (FRESH-1): an input product (never sold) with its family and, optionally, what it becomes.
  const [input, setInput] = useState(false);
  const [family, setFamily] = useState('');
  const [becomes, setBecomes] = useState<ProductSummary[]>([]);
  const families = useFamilies();
  const ids = useId();

  useEffect(() => {
    if (!open || !line) return;
    setInput(false);
    setFamily('');
    setBecomes([]);
    setName(line.description);
    // Retail price is never guessed — the operator sets it deliberately,
    // same as the Svelte reference's create form.
    setPrice(null);
    setCost(
      derivedPerEachCents(line) ?? (line.unitCostCents > 0 ? line.unitCostCents : null)
    );
    setBarcode(line.upc);
    setVendorSku(line.sku);
    setUnitsPerCase(line.unitsPerCase);
  }, [open, line]);

  // TASK-0191 round 3 (review fix, finding 2). This dialog is one persistent
  // component instance reused across lines — `line` is just its current
  // prop. TanStack Query v5 rebinds a PENDING mutation's options (including
  // `onSuccess`) to the latest render on every render of this component, so
  // reading `line` inside `onSuccess` reads whatever line the dialog is
  // showing WHEN THE REQUEST RESOLVES, not the one it was submitted for.
  // Sequence: open for A → click Create (request fires) → close before it
  // resolves → reopen for B → A's request resolves → the product used to
  // attach to B, with a toast that falsely named A's product as attached
  // "to the line" (whichever one was current). Fixed by threading the
  // target line's key through the mutation's `variables` — captured at
  // `mutate()` time, fixed for the life of that call, immune to rebinding.
  const create = useMutation({
    mutationFn: async (vars: {
      targetKey: number;
      prefill: CreateProductPrefill;
      becomes: number[];
    }) => {
      const result = await createProductAndLink(vendorId, vars.prefill);
      if (vars.becomes.length > 0) {
        try {
          await api.put(`/products/${result.product.id}/becomes`, {
            productIds: vars.becomes,
          });
        } catch (error) {
          return {
            ...result,
            linkWarning:
              result.linkWarning ??
              `"Usually becomes" was not saved: ${errorMessage(error)}. Set it on the product page.`,
          };
        }
      }
      return result;
    },
    onSuccess: ({ product, linkWarning }, vars) => {
      onCreated(vars.targetKey, product);
      // `line` here IS the latest prop (see above) — that's exactly what we
      // want for this decision: only auto-close if the dialog is still
      // showing the line this create was FOR. If the operator already
      // reopened it for a different line, force-closing here would yank
      // that dialog away out from under them.
      if (line && line.key === vars.targetKey) onOpenChange(false);
      if (linkWarning) toast.warning(`Created "${product.name}". ${linkWarning}`);
      else toast.success(`Created "${product.name}" and attached it to the line.`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const canCreate =
    name.trim() !== '' && (input ? family !== '' : price != null && price >= 0);
  const submit = () => {
    if (!line) return;
    const prefill: CreateProductPrefill = {
      name: name.trim(),
      price: input ? 0 : (price ?? 0),
      ...(input ? { purchasingOnly: true, productionFamily: family } : {}),
      cost,
      barcode: barcode.trim() || null,
      vendorSku: vendorSku.trim() || null,
      unitsPerCase,
      caseCost: line.caseMode ? line.unitCostCents : null,
      // Finding 1: a `lb` line must produce a weight-sold product, or its
      // auto-attach immediately trips the unit-mismatch guard below.
      unit: line.unit,
    };
    create.mutate({
      targetKey: line.key,
      prefill,
      becomes: becomes.map((b) => Number(b.id)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create product"
      description={
        line ? `From “${line.description}” — attaches to this line on save.` : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!canCreate}
            onClick={submit}
          >
            Create &amp; attach
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" required className="col-span-2">
          <Input
            size="sm"
            autoFocus
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        <div className="col-span-2">
          <Checkbox
            checked={input}
            onCheckedChange={setInput}
            label="Input product (bought for the butcher or kitchen, never sold as is)"
          />
        </div>
        {input ? (
          <Field
            label="Family"
            htmlFor={`${ids}-family`}
            required
            hint="Chicken, beef… the family of what it becomes."
          >
            <Select
              id={`${ids}-family`}
              size="sm"
              value={family}
              onChange={(e) => setFamily(e.currentTarget.value)}
            >
              <option value="">—</option>
              {activeFamilies(families.data).map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field label="Retail price" htmlFor={`${ids}-price`} required>
            <MoneyInput
              id={`${ids}-price`}
              size="sm"
              value={price}
              onValueChange={setPrice}
            />
          </Field>
        )}
        <Field label="Cost">
          <MoneyInput size="sm" value={cost} onValueChange={setCost} />
        </Field>
        <Field label="Barcode / UPC">
          <Input
            size="sm"
            value={barcode}
            onChange={(e) => setBarcode(e.currentTarget.value)}
          />
        </Field>
        <Field label="Vendor SKU">
          <Input
            size="sm"
            value={vendorSku}
            onChange={(e) => setVendorSku(e.currentTarget.value)}
          />
        </Field>
        <Field label="Units per case">
          <Input
            size="sm"
            mono
            inputMode="numeric"
            value={unitsPerCase == null ? '' : String(unitsPerCase)}
            onChange={(e) => {
              const raw = e.currentTarget.value.trim();
              if (raw === '') {
                setUnitsPerCase(null);
                return;
              }
              const value = Number(raw);
              if (Number.isFinite(value) && value > 0) setUnitsPerCase(Math.round(value));
            }}
          />
        </Field>
      </div>
      {input ? (
        <div className="mt-3 space-y-2">
          <Field
            label="Usually becomes"
            hint="Optional. The sold product this input turns into; a hint for finding things, nothing else."
          >
            {becomes.length > 0 ? (
              <div className="mb-1.5 flex flex-wrap gap-1.5">
                {becomes.map((b) => (
                  <Badge key={b.id}>
                    {b.name}
                    <button
                      type="button"
                      className="ml-1"
                      aria-label={`Remove ${b.name}`}
                      onClick={() =>
                        setBecomes((prev) => prev.filter((x) => x.id !== b.id))
                      }
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
            <LinkPicker
              side="becomes"
              family={family || null}
              excludeIds={new Set(becomes.map((b) => Number(b.id)))}
              onPick={(row) => setBecomes((prev) => [...prev, row])}
            />
          </Field>
          <Consequences tone="info" items={[INPUT_CONSEQUENCE]} />
        </div>
      ) : null}
      {vendorId == null ? (
        <p className="text-2xs text-ink-faint mt-3">
          No vendor selected on this import — the product is created but not linked to a
          vendor.
        </p>
      ) : null}
    </Dialog>
  );
}
